import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faVideo, faPhone, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import MenuItem from '@mui/material/MenuItem';
import useParticipants from '../../hooks/useParticipants';
import useRoom from '../../hooks/useRoom';
import useTracks from '../../hooks/useTracks';
import useTrackEnabled from '../../hooks/useTrackEnabled';
import HostParticipant from './HostParticipant';
import Participants from './Participants';
import ParticipantsInfo from './ParticipantsInfo';
import { createLocalVideoTrack } from 'twilio-video';
import { isAudioAppointment, getParticipantName } from '../../common/utility';
import styles from './RoomBody.module.scss';
import MenuButton from '../Common/MenuButton';

const INITIAL_STATE = {
    muted: false,
    videoOff: false,
};

function RoomBody({ appointment, onLeave }) {
    const [{ muted, videoOff }, setState] = useState(INITIAL_STATE);
    const [isGridView, setIsGridView] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
    const [mainViewParticipant, setMainViewParticipant] = useState(null);
    const { room } = useRoom();
    const { tracks } = useTracks(room?.localParticipant, false, isAudioAppointment(appointment));
    const isAudioEnabled = useTrackEnabled(tracks, 'audio');
    const { hostParticipant, participants } = useParticipants(appointment);
    const viewOptionsRef = useRef(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 576);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (hostParticipant && !mainViewParticipant) {
            setMainViewParticipant(hostParticipant);
        }
    }, [hostParticipant]);

    async function handleTurnOffCameraClick() {
        if (videoOff) {
            const videoTrack = await createLocalVideoTrack();
            room.localParticipant.publishTrack(videoTrack);
            setState((prevState) => ({
                ...prevState,
                videoOff: false,
            }));
            return;
        }
        room.localParticipant.tracks.forEach((publication) => {
            if (publication.track?.kind === 'video') {
                room.localParticipant.emit('localTrackUnsubscribed', publication.track);
                publication.track.stop();
                publication.unpublish();
                room.localParticipant.emit('localTrackUnpublished', publication);
                setState((prevState) => ({
                    ...prevState,
                    videoOff: true,
                }));
            }
        });
    }

    async function handleMuteClick() {
        room.localParticipant.tracks.forEach((publication) => {
            if (publication.track?.kind === 'audio') {
                if (muted) {
                    publication.track.enable();
                } else {
                    publication.track.disable();
                }
            }
        });
    }

    useEffect(() => {
        let nextMuted = true;
        if (isAudioEnabled) {
            nextMuted = false;
        }

        setState((prevState) => ({
            ...prevState,
            muted: nextMuted,
        }));
    }, [isAudioEnabled]);

    const handleMenuItemClick = (selectedItem) => {
        if (selectedItem === 'grid') {
            setIsGridView(true);
        } else {
            const participant = participants.find((participant) => participant.sid === selectedItem);
            setMainViewParticipant(participant);
            setIsGridView(false);
        }

        viewOptionsRef.current.closeMenu();
    };

    const viewOptionsButtonContent = () => (
        <div className={styles['button-content']}>
            {!isMobile && <span>View Options</span>}
            <FontAwesomeIcon icon={faChevronDown} />
        </div>
    );

    const viewOptionsItems = () => {
        const items = participants.map((participant) => {
            const participantName = getParticipantName(participant.identity, appointment);

            return (
                <MenuItem key={participant.sid} onClick={handleMenuItemClick.bind(this, participant.sid)}>
                    {participantName}
                </MenuItem>
            );
        });

        const gridItem = (
            <MenuItem key='grid' onClick={handleMenuItemClick.bind(this, 'grid')}>
                Grid
            </MenuItem>
        );

        return [gridItem, ...items];
    };

    return (
        <section className={styles['room-body']}>
            <div>
                <div className={styles.participants}>
                    {isGridView ? (
                        <Participants
                            hostParticipant={hostParticipant}
                            participants={participants}
                            appointment={appointment}
                            gridView={true}
                        />
                    ) : (
                        <>
                            {hostParticipant && (
                                <HostParticipant participant={mainViewParticipant} appointment={appointment} />
                            )}
                            <Participants
                                hostParticipant={mainViewParticipant}
                                participants={participants}
                                appointment={appointment}
                            />
                        </>
                    )}
                </div>
                <article className={styles.actions}>
                    <div className={`${muted ? styles.inactive : ''} ${styles.action}`} onClick={handleMuteClick}>
                        <FontAwesomeIcon icon={faMicrophone} />
                    </div>
                    {(!isAudioAppointment(appointment) || null) && (
                        <div
                            className={`${videoOff ? styles.inactive : ''} ${styles.action}`}
                            onClick={handleTurnOffCameraClick}
                        >
                            <FontAwesomeIcon icon={faVideo} />
                        </div>
                    )}
                    <div className={`${styles.inactive} ${styles.action}`} onClick={onLeave}>
                        <FontAwesomeIcon icon={faPhone} />
                    </div>
                    <div className={styles['view-options-wrapper']}>
                        <MenuButton
                            ref={viewOptionsRef}
                            classes={{
                                button: isMobile ? styles.action : styles['view-options'],
                                menu: styles['view-options__menu'],
                            }}
                            buttonContent={viewOptionsButtonContent}
                            isMobile={isMobile}
                        >
                            {viewOptionsItems()}
                        </MenuButton>
                    </div>
                </article>
                <ParticipantsInfo
                    appointment={appointment}
                    participants={participants.filter((participant) => participant)}
                />
            </div>
        </section>
    );
}

RoomBody.displayName = RoomBody.name;

export default RoomBody;
